import React, { useState, useEffect } from "react";
import SelectionSummary from "../../pages/Enroll/SelectionSummary";
import InputField from "./inputs/InputField";
import ProgressBar from "./ProgressBar";
import "./JotFormEmbed.scss";
import CollapseButton from "./inputs/CollapseButton";
import {
	JLR_CHANNELS,
	JLR_DEALER_CODE_NAME,
	JLR_FORM_ID,
	JLR_TALENT_AGREEMENT_VALUE,
	createEmptyJlrChannelState,
} from "../../data/jlrEnrollChannels";
import jlrChannelDisplay from "../../data/jlrChannelDisplay.json";
import dealerCodeToName from "../../data/dealers.json";

function getJlrChannelsMeta() {
	const d = jlrChannelDisplay;
	if (d && typeof d.channels === "object" && d.channels !== null) {
		return d.channels;
	}
	return d;
}

function getJlrChannelDisplay(channel) {
	const row = getJlrChannelsMeta()[channel.id] || {};
	const displayLabel =
		typeof row.displayLabel === "string" && row.displayLabel.trim()
			? row.displayLabel.trim()
			: channel.label;
	const pill = typeof row.pill === "string" ? row.pill.trim() : "";
	const copy = typeof row.copy === "string" ? row.copy.trim() : "";
	const min =
		typeof row.min === "string" && row.min.trim() ? Number(row.min) : null;
	return { displayLabel, pill, copy, min };
}

/** Build [{ title, channels }] from `jlrChannelDisplay.groups`; leftover channels get one untitled section. */
function buildJlrChannelSections() {
	const d = jlrChannelDisplay;
	const groupDefs = Array.isArray(d.groups) ? d.groups : [];
	if (groupDefs.length === 0) {
		return [{ title: null, channels: [...JLR_CHANNELS] }];
	}
	const grouped = new Set();
	const sections = [];
	for (const g of groupDefs) {
		const ids = Array.isArray(g.channelIds) ? g.channelIds : [];
		ids.forEach((id) => grouped.add(id));
		const chans = ids
			.map((id) => JLR_CHANNELS.find((c) => c.id === id))
			.filter(Boolean);
		if (chans.length) {
			sections.push({
				title: typeof g.title === "string" ? g.title.trim() : "",
				channels: chans,
			});
		}
	}
	const ungrouped = JLR_CHANNELS.filter((c) => !grouped.has(c.id));
	if (ungrouped.length) {
		sections.push({ title: null, channels: ungrouped });
	}
	return sections.length
		? sections
		: [{ title: null, channels: [...JLR_CHANNELS] }];
}

/** Parse user input to a finite number, or NaN. */
function parseBudgetAmount(raw) {
	const cleaned = String(raw ?? "")
		.replace(/,/g, "")
		.replace(/[^0-9.-]/g, "");
	const n = parseFloat(cleaned);
	return Number.isFinite(n) ? n : NaN;
}

/** Round to cents (2 decimals), non-negative. Empty / invalid → "". */
function normalizeBudgetMoney(raw) {
	const n = parseBudgetAmount(raw);
	if (!Number.isFinite(n) || n < 0) return "";
	const cents = Math.round(n * 100) / 100;
	if (cents === 0) return "0";
	const fixed = cents.toFixed(2);
	return fixed.endsWith(".00") ? fixed.slice(0, -3) : fixed;
}

function formatUsdMoney(amount) {
	if (!Number.isFinite(amount)) return "";
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
	}).format(amount);
}

/** Same layout as editing: amount next to separate "$" span (no leading $). */
function formatUsdAmountDigits(amount) {
	const full = formatUsdMoney(amount);
	if (!full) return "";
	return full.replace(/^\$\s*/, "").trim();
}

/** After blur: show currency in the shell (not while editing this channel). */
function shouldShowJlrBudgetMoneyDisplay(budget, editingChannelId, channelId) {
	if (editingChannelId === channelId) return false;
	const b = String(budget ?? "").trim();
	if (!b) return false;
	const norm = normalizeBudgetMoney(b);
	return norm !== "" && norm === b;
}

/** Resolve dealership display name from `dealers.json` keys (handles alphanumeric codes, leading zeros, and 5-digit pad). */
function resolveDealershipNameFromDealerCode(raw, map) {
	const trimmed = String(raw ?? "").trim();
	if (!trimmed) return null;
	// Exact match
	if (map[trimmed]) return map[trimmed];
	// Case-insensitive exact match
	const upper = trimmed.toUpperCase();
	if (map[upper]) return map[upper];
	// Numeric-only fallback: strip non-digits and try padding variants
	const digits = trimmed.replace(/\D/g, "");
	if (digits) {
		if (map[digits]) return map[digits];
		const padded5 = digits.padStart(5, "0");
		if (map[padded5]) return map[padded5];
		const noLeading = digits.replace(/^0+/, "") || "0";
		if (map[noLeading]) return map[noLeading];
		const padNoLeading = noLeading.padStart(5, "0");
		if (map[padNoLeading]) return map[padNoLeading];
	}
	return null;
}

const FORM_ID = JLR_FORM_ID;

/** JotForm full-name field `id` / `name` for each optional “Add another contact” slot (order matches submit mapping). */
const ADDITIONAL_DEALERSHIP_CONTACT_FIELDS = [
	{ id: "id_22", name: "q22_dealershipContact22" },
	{ id: "id_56", name: "q56_dealershipContact56" },
	{ id: "id_61", name: "q61_dealershipContact61" },
	{ id: "id_66", name: "q66_dealershipContact66" },
	{ id: "id_71", name: "q71_dealershipContact71" },
];

const JotFormComponent = () => {
	const [currentPage, setCurrentPage] = useState(1);
	const [channels, setChannels] = useState(createEmptyJlrChannelState);
	const [editingBudgetChannelId, setEditingBudgetChannelId] = useState(null);

	useEffect(() => {
		if (!editingBudgetChannelId) return;
		const ch = JLR_CHANNELS.find((x) => x.id === editingBudgetChannelId);
		if (!ch) return;
		const id = `input_${ch.budgetQid}`;
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				document.getElementById(id)?.focus();
			});
		});
	}, [editingBudgetChannelId]);

	const [dealerInfo, setDealerInfo] = useState({
		dealerCode: "",
		dealershipName: "",
		dealershipContact: { first: "", last: "" },
		emailAddress: "",
		phoneNumber: { full: "" },
		dealershipContact22: { first: "", last: "" },
		emailAddress23: "",
		phoneNumber24: { full: "" },

		// Additional contacts matching JotForm field IDs
		dealershipContact56: { first: "", last: "" },
		emailAddress57: "",
		phoneNumber58: { full: "" },

		dealershipContact61: { first: "", last: "" },
		emailAddress62: "",
		phoneNumber63: { full: "" },

		dealershipContact66: { first: "", last: "" },
		emailAddress67: "",
		phoneNumber68: { full: "" },

		dealershipContact71: { first: "", last: "" },
		emailAddress72: "",
		phoneNumber73: { full: "" },
	});

	// eslint-disable-next-line no-unused-vars
	const [isSubmitting, setIsSubmitting] = useState(false);
	// eslint-disable-next-line no-unused-vars
	const [submitStatus, setSubmitStatus] = useState(null);

	// Add validation state
	const [validation, setValidation] = useState({
		emailAddress: true,
		emailAddress23: true,
		emailAddress72: true,
		phoneNumber: true,
		phoneNumber24: true,
		phoneNumber73: true,
	});

	const [packageError, setPackageError] = useState(false);
	const [jlrBudgetNextAttempted, setJlrBudgetNextAttempted] = useState(false);
	const [phoneError, setPhoneError] = useState(false);
	const [emailError, setEmailError] = useState(false);

	// Update additional contacts state to handle up to 5 contacts
	const [additionalContacts, setAdditionalContacts] = useState([]);
	const [openContactSections, setOpenContactSections] = useState([]);

	// Add validation functions
	const validateEmail = (email) => {
		const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return re.test(email);
	};

	const validatePhone = (phone) => {
		const re = /^\(\d{3}\)\s\d{3}-\d{4}$/;
		return re.test(phone);
	};

	const formatPhoneNumber = (value) => {
		// Remove all non-digits
		const numbers = value.replace(/\D/g, "");

		// Format the number as (XXX) XXX-XXXX
		if (numbers.length >= 10) {
			return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(
				6,
				10,
			)}`;
		}
		return value;
	};

	// Update the phone number handler
	const handlePhoneChange = (e, field, contactIndex) => {
		const formattedNumber = formatPhoneNumber(e.target.value);
		const isValid = validatePhone(formattedNumber);

		if (typeof contactIndex === "number") {
			// Handle additional contacts
			const newContacts = [...additionalContacts];
			newContacts[contactIndex].phoneNumber.full = formattedNumber;
			newContacts[contactIndex].phoneValid = isValid;
			setAdditionalContacts(newContacts);
		} else {
			// Handle primary contacts as before
			if (field === "primary") {
				setDealerInfo((prev) => ({
					...prev,
					phoneNumber: { full: formattedNumber },
				}));
				setValidation((prev) => ({ ...prev, phoneNumber: isValid }));
			} else if (field === "secondary") {
				setDealerInfo((prev) => ({
					...prev,
					phoneNumber24: { full: formattedNumber },
				}));
				setValidation((prev) => ({ ...prev, phoneNumber24: isValid }));
			} else if (field === "tertiary") {
				setDealerInfo((prev) => ({
					...prev,
					phoneNumber73: { full: formattedNumber },
				}));
				setValidation((prev) => ({ ...prev, phoneNumber73: isValid }));
			}
		}
	};

	// Update the email handler
	const handleEmailChange = (e, field, contactIndex) => {
		const value = e.target.value;
		const isValid = validateEmail(value);

		if (typeof contactIndex === "number") {
			// Handle additional contacts
			const newContacts = [...additionalContacts];
			newContacts[contactIndex].emailAddress = value;
			newContacts[contactIndex].emailValid = isValid;
			setAdditionalContacts(newContacts);
		} else {
			// Handle primary contacts as before
			if (field === "primary") {
				setDealerInfo((prev) => ({ ...prev, emailAddress: value }));
				setValidation((prev) => ({ ...prev, emailAddress: isValid }));
			} else if (field === "secondary") {
				setDealerInfo((prev) => ({ ...prev, emailAddress23: value }));
				setValidation((prev) => ({ ...prev, emailAddress23: isValid }));
			} else if (field === "tertiary") {
				setDealerInfo((prev) => ({ ...prev, emailAddress72: value }));
				setValidation((prev) => ({ ...prev, emailAddress72: isValid }));
			}
		}
	};

	const toggleJlrChannel = (id) => {
		setEditingBudgetChannelId((cur) => (cur === id ? null : cur));
		setChannels((prev) => {
			const next = {
				...prev,
				[id]: { ...prev[id], checked: !prev[id].checked },
			};
			if (!next[id].checked) {
				next[id] = { ...next[id], budget: "" };
			} else {
				const ch = JLR_CHANNELS.find((c) => c.id === id);
				const { min } = ch ? getJlrChannelDisplay(ch) : { min: null };
				if (min !== null) {
					next[id] = { ...next[id], budget: String(min) };
				}
			}
			return next;
		});
		setPackageError(false);
	};

	const setJlrChannelBudget = (id, budget) => {
		setChannels((prev) => ({
			...prev,
			[id]: { ...prev[id], budget },
		}));
	};

	const handleJlrBudgetChange = (id, value) => {
		if (value === "" || value == null) {
			setJlrChannelBudget(id, "");
			return;
		}
		const n = Number(value);
		if (!Number.isFinite(n)) return;
		if (n < 0) {
			setJlrChannelBudget(id, "0");
			return;
		}
		setJlrChannelBudget(id, value);
	};

	const handleJlrBudgetBlur = (id) => {
		setChannels((prev) => {
			const normalized = normalizeBudgetMoney(prev[id]?.budget);
			const ch = JLR_CHANNELS.find((c) => c.id === id);
			const { min } = ch ? getJlrChannelDisplay(ch) : { min: null };
			let final = normalized;
			if (min !== null) {
				const n = parseBudgetAmount(normalized);
				if (normalized === "" || !Number.isFinite(n) || n < min) {
					final = normalizeBudgetMoney(String(min));
				}
			}
			return { ...prev, [id]: { ...prev[id], budget: final } };
		});
	};

	/** Add/subtract whole dollars from the current amount (spinner / arrows), not step-grid snap. */
	const adjustJlrBudgetByFiveHundreds = (id, deltaDollars) => {
		setChannels((prev) => {
			const raw = prev[id]?.budget ?? "";
			const n = parseBudgetAmount(raw);
			const base = Number.isFinite(n) ? n : 0;
			const ch = JLR_CHANNELS.find((c) => c.id === id);
			const { min } = ch ? getJlrChannelDisplay(ch) : { min: null };
			const floor = min !== null ? min : 0;
			const next = Math.max(floor, base + deltaDollars);
			return {
				...prev,
				[id]: {
					...prev[id],
					budget: normalizeBudgetMoney(String(next)),
				},
			};
		});
	};

	const handleJlrBudgetStepKeyDown = (id, e) => {
		if (e.key === "Enter") {
			e.preventDefault();
			return;
		}
		if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
		e.preventDefault();
		adjustJlrBudgetByFiveHundreds(id, e.key === "ArrowUp" ? 500 : -500);
	};

	// Handler for navigating to the next page
	const handleNext = (e) => {
		e.preventDefault();

		const anyChannel = JLR_CHANNELS.some((c) => channels[c.id]?.checked);
		if (!anyChannel) {
			setPackageError(true);
			window.scrollTo({ top: 0, behavior: "auto" });
			return;
		}

		const missingBudget = JLR_CHANNELS.some((c) => {
			if (!channels[c.id]?.checked) return false;
			const norm = normalizeBudgetMoney(channels[c.id].budget);
			return norm === "";
		});
		if (missingBudget) {
			setJlrBudgetNextAttempted(true);
			window.scrollTo({ top: 0, behavior: "auto" });
			return;
		}

		setJlrBudgetNextAttempted(false);
		setChannels((prev) => {
			const next = { ...prev };
			JLR_CHANNELS.forEach((ch) => {
				if (!next[ch.id]?.checked) return;
				next[ch.id] = {
					...next[ch.id],
					budget: normalizeBudgetMoney(next[ch.id].budget),
				};
			});
			return next;
		});

		setPackageError(false);
		setEditingBudgetChannelId(null);
		setCurrentPage(2);
		window.scrollTo({ top: 0, behavior: "auto" });
	};

	// Add handleBack function near other handlers
	const handleBack = () => {
		setEditingBudgetChannelId(null);
		setJlrBudgetNextAttempted(false);
		setCurrentPage(1);
		window.scrollTo({ top: 0, behavior: "auto" });
	};

	// Function to add contact section
	const handleAddContact = () => {
		if (additionalContacts.length < 5) {
			setAdditionalContacts([
				...additionalContacts,
				{
					dealershipContact: { first: "", last: "" },
					emailAddress: "",
					phoneNumber: { full: "" },
					emailValid: true,
					phoneValid: true,
				},
			]);
			setOpenContactSections([
				...openContactSections,
				additionalContacts.length,
			]);
		}
	};

	// Function to remove contact section
	const handleRemoveContact = (index) => {
		const newContacts = additionalContacts.filter((_, i) => i !== index);
		setAdditionalContacts(newContacts);
		// Remove the index from openContactSections and reindex remaining sections
		const newOpenSections = openContactSections
			.filter((i) => i !== index)
			.map((i) => (i > index ? i - 1 : i));
		setOpenContactSections(newOpenSections);
	};

	// Update handleSubmit to merge additionalContacts with dealerInfo before storing
	const handleSubmit = async (e) => {
		e.preventDefault();
		if (currentPage !== 2) {
			return;
		}

		// Check if all validations are true
		const allValid =
			Object.values(validation).every((valid) => valid) &&
			additionalContacts.every(
				(contact) => contact.emailValid && contact.phoneValid,
			);

		if (!allValid) {
			alert("Please fill out all required fields correctly.");
			return;
		}

		setIsSubmitting(true);

		try {
			// Map additional contacts to dealerInfo structure before storing
			const updatedDealerInfo = { ...dealerInfo };

			additionalContacts.forEach((contact, index) => {
				const fieldMappings = [
					{
						contact: "dealershipContact22",
						email: "emailAddress23",
						phone: "phoneNumber24",
					},
					{
						contact: "dealershipContact56",
						email: "emailAddress57",
						phone: "phoneNumber58",
					},
					{
						contact: "dealershipContact61",
						email: "emailAddress62",
						phone: "phoneNumber63",
					},
					{
						contact: "dealershipContact66",
						email: "emailAddress67",
						phone: "phoneNumber68",
					},
					{
						contact: "dealershipContact71",
						email: "emailAddress72",
						phone: "phoneNumber73",
					},
				];

				if (index < fieldMappings.length) {
					const mapping = fieldMappings[index];
					updatedDealerInfo[mapping.contact] =
						contact.dealershipContact;
					updatedDealerInfo[mapping.email] = contact.emailAddress;
					updatedDealerInfo[mapping.phone] = contact.phoneNumber;
				}
			});

			// Store the merged data
			localStorage.setItem(
				"dealerInfo",
				JSON.stringify(updatedDealerInfo),
			);

			const formData = {
				q75_typeA: "",
				[JLR_DEALER_CODE_NAME]: updatedDealerInfo.dealerCode,
				q18_dealershipName: updatedDealerInfo.dealershipName,
				"q14_dealershipContact[first]":
					updatedDealerInfo.dealershipContact.first,
				"q14_dealershipContact[last]":
					updatedDealerInfo.dealershipContact.last,
				q19_emailAddress: updatedDealerInfo.emailAddress,
				"q20_phoneNumber[full]": updatedDealerInfo.phoneNumber.full,

				"q56_dealershipContact56[first]":
					updatedDealerInfo.dealershipContact56.first,
				"q56_dealershipContact56[last]":
					updatedDealerInfo.dealershipContact56.last,
				q57_emailAddress57: updatedDealerInfo.emailAddress57,
				"q58_phoneNumber58[full]": updatedDealerInfo.phoneNumber58.full,

				"q61_dealershipContact61[first]":
					updatedDealerInfo.dealershipContact61.first,
				"q61_dealershipContact61[last]":
					updatedDealerInfo.dealershipContact61.last,
				q62_emailAddress62: updatedDealerInfo.emailAddress62,
				"q63_phoneNumber63[full]": updatedDealerInfo.phoneNumber63.full,

				"q66_dealershipContact66[first]":
					updatedDealerInfo.dealershipContact66.first,
				"q66_dealershipContact66[last]":
					updatedDealerInfo.dealershipContact66.last,
				q67_emailAddress67: updatedDealerInfo.emailAddress67,
				"q68_phoneNumber68[full]": updatedDealerInfo.phoneNumber68.full,

				"q71_dealershipContact71[first]":
					updatedDealerInfo.dealershipContact71.first,
				"q71_dealershipContact71[last]":
					updatedDealerInfo.dealershipContact71.last,
				q72_emailAddress72: updatedDealerInfo.emailAddress72,
				"q73_phoneNumber73[full]": updatedDealerInfo.phoneNumber73.full,

				"q22_dealershipContact22[first]":
					updatedDealerInfo.dealershipContact22.first,
				"q22_dealershipContact22[last]":
					updatedDealerInfo.dealershipContact22.last,
				q23_emailAddress23: updatedDealerInfo.emailAddress23,
				"q24_phoneNumber24[full]": updatedDealerInfo.phoneNumber24.full,
			};

			JLR_CHANNELS.forEach((c) => {
				const row = channels[c.id];
				if (row?.checked) {
					formData[`${c.checkboxName}[]`] = c.checkboxValue;
					formData[c.budgetName] = normalizeBudgetMoney(row.budget);
				}
			});

			// Map additional contacts to specific JotForm fields
			additionalContacts.forEach((contact, index) => {
				const fieldMappings = [
					{
						contact: "q56_dealershipContact56",
						email: "q57_emailAddress57",
						phone: "q58_phoneNumber58",
					},
					{
						contact: "q61_dealershipContact61",
						email: "q62_emailAddress62",
						phone: "q63_phoneNumber63",
					},
					{
						contact: "q66_dealershipContact66",
						email: "q67_emailAddress67",
						phone: "q68_phoneNumber68",
					},
					{
						contact: "q71_dealershipContact71",
						email: "q72_emailAddress72",
						phone: "q73_phoneNumber73",
					},
					{
						contact: "q22_dealershipContact22",
						email: "q23_emailAddress23",
						phone: "q24_phoneNumber24",
					},
				];

				if (index < fieldMappings.length) {
					const mapping = fieldMappings[index];
					formData[`${mapping.contact}[first]`] =
						contact.dealershipContact.first;
					formData[`${mapping.contact}[last]`] =
						contact.dealershipContact.last;
					formData[mapping.email] = contact.emailAddress;
					formData[`${mapping.phone}[full]`] =
						contact.phoneNumber.full;
				}
			});

			localStorage.setItem(
				"enrollmentSelections",
				JSON.stringify({ variant: "jlr", channels }),
			);

			// Submit the form
			const form = document.getElementById(FORM_ID);
			form.action = `https://submit.jotform.com/submit/${FORM_ID}`;
			form.method = "post";

			Object.entries(formData).forEach(([key, value]) => {
				const exists = [...form.querySelectorAll("[name]")].some(
					(el) => el.getAttribute("name") === key,
				);
				if (!exists) {
					const input = document.createElement("input");
					input.type = "hidden";
					input.name = key;
					input.value = value == null ? "" : String(value);
					form.appendChild(input);
				}
			});

			form.submit();
		} catch (error) {
			console.error("Form submission failed:", error);
			setSubmitStatus("error");
			setIsSubmitting(false);
		}
	};
	const isSubmitDisabled = Object.values(validation).some((valid) => !valid);

	// Render additional contacts section
	const renderAdditionalContacts = () => {
		return (
			<div className="additional-contacts-section">
				{additionalContacts.map((contact, index) => (
					<div key={index}>
						<li
							className="form-input-wide input-collapse-question"
							style={{ clear: "both" }}>
							{}
						</li>

						{openContactSections.includes(index) && (
							<div className="additional-contact-collapse">
								<div
									className="form-line jlr-channel-group-header"
									role="presentation">
									<div className="jlr-channel-group-title-row">
										<h2 className="jlr-channel-group-title">
											DEALERSHIP CONTACT
										</h2>
										<span
											className="jlr-channel-group-title-line"
											aria-hidden="true"
										/>
									</div>
								</div>
								<InputField
									id={
										ADDITIONAL_DEALERSHIP_CONTACT_FIELDS[
											index
										].id
									}
									isFullName
									fullNameHideMainLabel
									name={
										ADDITIONAL_DEALERSHIP_CONTACT_FIELDS[
											index
										].name
									}
									label="DEALERSHIP CONTACT"
									fullNameValues={contact.dealershipContact}
									fullNameOnChange={(field, value) => {
										const newContacts = [
											...additionalContacts,
										];
										newContacts[index].dealershipContact[
											field
										] = value;
										setAdditionalContacts(newContacts);
									}}
								/>

								<InputField
									type="email"
									label="EMAIL ADDRESS"
									value={contact.emailAddress}
									onChange={(e) =>
										handleEmailChange(e, null, index)
									}
									onBlur={() => {
										const isValidEmail = validateEmail(
											contact.emailAddress,
										);
										const newContacts = [
											...additionalContacts,
										];
										newContacts[index].emailValid =
											isValidEmail;
										setAdditionalContacts(newContacts);
									}}
									error={
										!contact.emailValid &&
										contact.emailAddress !== ""
									}
									errorMessage="Please enter a valid email address"
								/>
								{!contact.emailValid &&
									contact.emailAddress !== "" && (
										<p
											style={{
												color: "red",
												fontSize: "10px",
												marginTop: "0",
												marginLeft: "20px",
												padding: "0",
											}}>
											*Please enter a valid email address.
										</p>
									)}

								<InputField
									type="tel"
									label="PHONE NUMBER"
									value={contact.phoneNumber.full}
									onChange={(e) => {
										const value = e.target.value.replace(
											/\D/g,
											"",
										);
										handlePhoneChange(
											{ target: { value } },
											null,
											index,
										);
									}}
									onBlur={() => {
										const isValidPhone = validatePhone(
											contact.phoneNumber.full,
										);
										const newContacts = [
											...additionalContacts,
										];
										newContacts[index].phoneValid =
											isValidPhone;
										setAdditionalContacts(newContacts);
									}}
									placeholder="(000) 000-0000"
									error={
										!contact.phoneValid &&
										contact.phoneNumber.full !== ""
									}
									errorMessage="Please enter a valid phone number"
								/>
								{!contact.phoneValid &&
									contact.phoneNumber.full !== "" && (
										<p
											style={{
												color: "red",
												fontSize: "10px",
												marginTop: "0",
												marginLeft: "20px",
												padding: "0",
											}}>
											*Please enter a complete phone
											number.
										</p>
									)}

								<div className="remove-contact-button">
									<CollapseButton
										onClick={() =>
											handleRemoveContact(index)
										}
										variant="collapse"
									/>
								</div>
							</div>
						)}
					</div>
				))}

				{additionalContacts.length < 5 && (
					<CollapseButton
						onClick={handleAddContact}
						variant="expand"
					/>
				)}
			</div>
		);
	};

	const anyChannelSelected = JLR_CHANNELS.some(
		(c) => channels[c.id]?.checked,
	);

	return (
		<>
			<ProgressBar currentPage={currentPage} />
			<div className="enrollment-container">
				<div className="form-wrapper">
					<form
						onSubmit={handleSubmit}
						className="jotform-form"
						id={FORM_ID}
						acceptCharset="utf-8"
						autoComplete="on">
						<input type="hidden" name="formID" value={FORM_ID} />
						<input type="hidden" id="JWTContainer" value="" />
						<input
							type="hidden"
							id="cardinalOrderNumber"
							value=""
						/>

						<div role="main" className="form-all">
							{currentPage === 1 && (
								<ul
									className="form-section page-section"
									role="presentation">
									{buildJlrChannelSections().map(
										(section, sectionIndex) => (
											<React.Fragment
												key={`jlr-section-${sectionIndex}`}>
												{section.title ? (
													<li
														className="form-line jlr-channel-group-header"
														role="presentation">
														<div className="jlr-channel-group-title-row">
															<h2 className="jlr-channel-group-title">
																{section.title}
															</h2>
															<span
																className="jlr-channel-group-title-line"
																aria-hidden="true"
															/>
														</div>
													</li>
												) : null}
												{section.channels.map((c) => {
													const {
														displayLabel,
														pill,
														copy,
														min,
													} = getJlrChannelDisplay(c);
													const copyId = `jlr-channel-copy-${c.id}`;
													const rowBudget =
														channels[c.id]
															?.budget ?? "";
													const showBudgetMoneyOnly =
														shouldShowJlrBudgetMoneyDisplay(
															rowBudget,
															editingBudgetChannelId,
															c.id,
														);
													const budgetNormalized =
														normalizeBudgetMoney(
															rowBudget,
														);
													const budgetDisplayId = `jlr-budget-display-${c.budgetQid}`;
													const budgetMissingForRow =
														!!channels[c.id]
															?.checked &&
														budgetNormalized === "";
													const showBudgetRequiredOutline =
														budgetMissingForRow &&
														jlrBudgetNextAttempted;
													return (
														<li
															key={c.id}
															className={`form-line jlr-channel-row${
																channels[c.id]
																	?.checked
																	? " jlr-channel-row--selected"
																	: ""
															}${
																showBudgetRequiredOutline
																	? " jlr-channel-row--budget-required"
																	: ""
															}`}
															data-type="control_checkbox"
															id={`id_${c.checkboxQid}`}>
															<div
																id={`cid_${c.checkboxQid}`}
																className="form-input-wide jlr-channel-row__body"
																data-layout="full">
																<div
																	className="jlr-channel-row__control"
																	role="group"
																	aria-label={
																		displayLabel
																	}
																	data-component="checkbox">
																	<input
																		type="checkbox"
																		className="form-checkbox jlr-channel-row__checkbox"
																		id={`input_${c.checkboxQid}_0`}
																		name={`${c.checkboxName}[]`}
																		value={
																			c.checkboxValue
																		}
																		checked={
																			!!channels[
																				c
																					.id
																			]
																				?.checked
																		}
																		onChange={() =>
																			toggleJlrChannel(
																				c.id,
																			)
																		}
																		aria-describedby={
																			copy
																				? copyId
																				: undefined
																		}
																	/>
																	<div className="jlr-channel-row__text">
																		<div className="jlr-channel-row__header">
																			<label
																				className="jlr-channel-row__label"
																				id={`label_input_${c.checkboxQid}_0`}
																				htmlFor={`input_${c.checkboxQid}_0`}>
																				{
																					displayLabel
																				}
																			</label>
																			{pill ? (
																				<span className="jlr-channel-pill">
																					{
																						pill
																					}
																				</span>
																			) : null}
																		</div>
																		{copy ? (
																			<p
																				className="jlr-channel-copy"
																				id={
																					copyId
																				}>
																				{
																					copy
																				}
																			</p>
																		) : null}
																	</div>
																</div>
																{channels[c.id]
																	?.checked && (
																	<div
																		className="jlr-channel-row__budget"
																		id={`id_${c.budgetQid}`}
																		data-type="control_number">
																		<label
																			className="form-label form-label-top form-label-auto jlr-channel-row__budget-label"
																			htmlFor={
																				showBudgetMoneyOnly
																					? budgetDisplayId
																					: `input_${c.budgetQid}`
																			}
																			id={`label_${c.budgetQid}`}>
																			MONTHLY
																			BUDGET
																			<span className="form-required">
																				*
																			</span>
																		</label>
																		<div
																			id={`cid_${c.budgetQid}`}
																			className="form-input-wide jlr-budget-field__wide"
																			data-layout="full">
																			<span
																				className="form-sub-label-container jlr-budget-field"
																				style={{
																					verticalAlign:
																						"top",
																				}}>
																				<div className="jlr-budget-field__shell">
																					{showBudgetMoneyOnly ? (
																						<>
																							<input
																								type="hidden"
																								name={
																									c.budgetName
																								}
																								value={
																									budgetNormalized
																								}
																							/>
																							<span
																								className="jlr-budget-field__dollar"
																								aria-hidden="true">
																								$
																							</span>
																							<button
																								type="button"
																								id={
																									budgetDisplayId
																								}
																								className="jlr-budget-field__display"
																								aria-labelledby={`label_${c.budgetQid} sublabel_input_${c.budgetQid}`}
																								onClick={() =>
																									setEditingBudgetChannelId(
																										c.id,
																									)
																								}>
																								{formatUsdAmountDigits(
																									parseBudgetAmount(
																										budgetNormalized,
																									),
																								)}
																							</button>
																							<div className="jlr-budget-field__steppers">
																								<button
																									type="button"
																									tabIndex={
																										-1
																									}
																									className="jlr-budget-field__step jlr-budget-field__step--up"
																									aria-label="Increase monthly budget by 500 dollars"
																									onMouseDown={(
																										e,
																									) =>
																										e.preventDefault()
																									}
																									onClick={() =>
																										adjustJlrBudgetByFiveHundreds(
																											c.id,
																											500,
																										)
																									}
																								/>
																								<button
																									type="button"
																									tabIndex={
																										-1
																									}
																									className="jlr-budget-field__step jlr-budget-field__step--down"
																									aria-label="Decrease monthly budget by 500 dollars"
																									onMouseDown={(
																										e,
																									) =>
																										e.preventDefault()
																									}
																									onClick={() =>
																										adjustJlrBudgetByFiveHundreds(
																											c.id,
																											-500,
																										)
																									}
																								/>
																							</div>
																						</>
																					) : (
																						<>
																							<span
																								className="jlr-budget-field__dollar"
																								aria-hidden="true">
																								$
																							</span>
																							<input
																								type="number"
																								id={`input_${c.budgetQid}`}
																								name={
																									c.budgetName
																								}
																								className="form-number-input form-textbox jlr-budget-field__input"
																								data-type="input-number"
																								data-component="number"
																								placeholder="0"
																								min={
																									0
																								}
																								step="any"
																								aria-labelledby={`label_${c.budgetQid} sublabel_input_${c.budgetQid}`}
																								value={
																									channels[
																										c
																											.id
																									]
																										?.budget ??
																									""
																								}
																								onChange={(
																									e,
																								) =>
																									handleJlrBudgetChange(
																										c.id,
																										e
																											.target
																											.value,
																									)
																								}
																								onKeyDown={(
																									e,
																								) =>
																									handleJlrBudgetStepKeyDown(
																										c.id,
																										e,
																									)
																								}
																								onFocus={() =>
																									setEditingBudgetChannelId(
																										c.id,
																									)
																								}
																								onBlur={() => {
																									handleJlrBudgetBlur(
																										c.id,
																									);
																									setEditingBudgetChannelId(
																										null,
																									);
																								}}
																							/>
																							<div className="jlr-budget-field__steppers">
																								<button
																									type="button"
																									tabIndex={
																										-1
																									}
																									className="jlr-budget-field__step jlr-budget-field__step--up"
																									aria-label="Increase monthly budget by 500 dollars"
																									onMouseDown={(
																										e,
																									) =>
																										e.preventDefault()
																									}
																									onClick={() =>
																										adjustJlrBudgetByFiveHundreds(
																											c.id,
																											500,
																										)
																									}
																								/>
																								<button
																									type="button"
																									tabIndex={
																										-1
																									}
																									className="jlr-budget-field__step jlr-budget-field__step--down"
																									aria-label="Decrease monthly budget by 500 dollars"
																									onMouseDown={(
																										e,
																									) =>
																										e.preventDefault()
																									}
																									onClick={() =>
																										adjustJlrBudgetByFiveHundreds(
																											c.id,
																											-500,
																										)
																									}
																								/>
																							</div>
																						</>
																					)}
																				</div>
																				<label
																					className="form-sub-label"
																					htmlFor={
																						showBudgetMoneyOnly
																							? budgetDisplayId
																							: `input_${c.budgetQid}`
																					}
																					id={`sublabel_input_${c.budgetQid}`}
																					style={{
																						minHeight:
																							"13px",
																					}}>
																					per month{min !== null ? ` (Minimum: $${min.toLocaleString("en-US")})` : ""}
																				</label>
																			</span>
																		</div>
																	</div>
																)}
															</div>
														</li>
													);
												})}
											</React.Fragment>
										),
									)}
								</ul>
							)}

							{currentPage === 2 && (
								<ul
									className="form-section page-section page-two"
									role="presentation">
									{/* Page 2: Additional Fields */}
									<li
										className="form-line jlr-channel-group-header"
										role="presentation">
										<div
											className="jlr-channel-group-title-row"
											style={{ padding: "0 1rem" }}>
											<h2 className="jlr-channel-group-title">
												DEALERSHIP
											</h2>
											<span
												className="jlr-channel-group-title-line"
												aria-hidden="true"
											/>
										</div>
									</li>

									<InputField
										id="id_104"
										type="text"
										name={JLR_DEALER_CODE_NAME}
										label="DEALER CODE"
										value={dealerInfo.dealerCode}
										onChange={(e) => {
											setDealerInfo((prev) => ({
												...prev,
												dealerCode: e.target.value,
											}));
										}}
										onBlur={(e) => {
											const code = e.target.value;
											const name =
												resolveDealershipNameFromDealerCode(
													code,
													dealerCodeToName,
												);
											if (name) {
												setDealerInfo((prev) => ({
													...prev,
													dealershipName: name,
												}));
											}
										}}
										required
										dataType="input-textbox"
										dataComponent="textbox"
									/>

									{/* DEALERSHIP NAME Input */}
									<InputField
										id="id_18"
										type="text"
										pattern="[a-zA-Z]*"
										name="q18_dealershipName"
										label="DEALERSHIP NAME"
										value={dealerInfo.dealershipName}
										onChange={(e) =>
											setDealerInfo((prev) => ({
												...prev,
												dealershipName: e.target.value,
											}))
										}
										required
										dataType="input-textbox"
										dataComponent="textbox"
									/>

									<li
										className="form-line jlr-channel-group-header"
										role="presentation">
										<div
											className="jlr-channel-group-title-row"
											style={{ padding: "0 1rem" }}>
											<h2 className="jlr-channel-group-title">
												DEALERSHIP CONTACT
											</h2>
											<span
												className="jlr-channel-group-title-line"
												aria-hidden="true"
											/>
										</div>
									</li>

									<InputField
										id="id_14"
										isFullName
										fullNameHideMainLabel
										name="q14_dealershipContact"
										label="DEALERSHIP CONTACT"
										fullNameValues={
											dealerInfo.dealershipContact
										}
										fullNameOnChange={(field, value) => {
											// Remove any non-letter characters
											const filteredValue = value.replace(
												/[^a-zA-Z\s]/g,
												"",
											);
											setDealerInfo((prev) => ({
												...prev,
												dealershipContact: {
													...prev.dealershipContact,
													[field]: filteredValue,
												},
											}));
										}}
										required
									/>

									{/* EMAIL ADDRESS */}
									<InputField
										id="id_19"
										type="email"
										name="q19_emailAddress"
										label="EMAIL ADDRESS"
										value={dealerInfo.emailAddress}
										onChange={(e) =>
											handleEmailChange(e, "primary")
										}
										onBlur={() => {
											// Check if the email address is valid
											const isValidEmail = validateEmail(
												dealerInfo.emailAddress,
											);
											setValidation((prev) => ({
												...prev,
												emailAddress: isValidEmail,
											}));

											// Show the error message if the email is invalid
											if (
												!isValidEmail &&
												dealerInfo.emailAddress !== ""
											) {
												setEmailError(true); // Show the error message
											} else {
												setEmailError(false); // Hide the error message if valid
											}
										}}
										required
										dataComponent="email"
										autoComplete="section-input_19 email"
										error={
											!validation.emailAddress &&
											dealerInfo.emailAddress !== ""
										}
										errorMessage="Please enter a valid email address"
									/>
									{emailError &&
										!validation.emailAddress &&
										dealerInfo.emailAddress !== "" && (
											<p
												style={{
													color: "red",
													fontSize: "10px",
													marginTop: "0",
													marginLeft: "20px",
													padding: "0",
												}}>
												*Please enter a valid email
												address.
											</p>
										)}

									{/* PHONE NUMBER */}
									<InputField
										id="id_20"
										type="tel"
										name="q20_phoneNumber[full]"
										label="PHONE NUMBER"
										value={dealerInfo.phoneNumber.full}
										onChange={(e) => {
											const value =
												e.target.value.replace(
													/\D/g,
													"",
												); // Strip non-digit characters
											handlePhoneChange(
												{ target: { value } },
												"primary",
											);
										}}
										onBlur={() => {
											// Check if the phone number is complete (10 digits after stripping non-digits)
											const isValidPhone =
												dealerInfo.phoneNumber.full.replace(
													/\D/g,
													"",
												).length === 10;
											setValidation((prev) => ({
												...prev,
												phoneNumber: isValidPhone,
											}));

											// Show the error message if the phone number is not valid
											if (!isValidPhone) {
												setPhoneError(true); // Show the error message
											}
										}}
										required
										placeholder="(000) 000-0000"
										dataType="mask-number"
										dataComponent="phone"
										autoComplete="section-input_20 tel-national"
									/>
									{phoneError &&
										dealerInfo.phoneNumber.full.replace(
											/\D/g,
											"",
										).length !== 10 && (
											<p
												style={{
													color: "red",
													fontSize: "10px",
													marginTop: "0",
													marginLeft: "20px",
													padding: "0",
												}}>
												*Please enter a complete phone
												number.
											</p>
										)}

									{/* Additional Contacts Section */}
									{renderAdditionalContacts()}

									<li
										className="form-line jf-required"
										data-type="control_checkbox"
										id="id_25">
										<label
											className="form-label form-label-top form-label-auto"
											htmlFor="input_25_0"
											id="label_25"
											aria-hidden="false">
											<span className="form-required">
												*
											</span>
										</label>
										<div
											id="cid_25"
											className="form-input-wide jf-required"
											data-layout="full">
											<div
												className="form-single-column"
												role="group"
												aria-labelledby="label_25"
												data-component="checkbox">
												<span
													className="form-checkbox-item"
													style={{ clear: "left" }}>
													<span className="dragger-item"></span>
													<input
														aria-describedby="label_25"
														type="checkbox"
														className="form-checkbox validate[required]"
														id="input_25_0"
														name="q25_input28[]"
														value={
															JLR_TALENT_AGREEMENT_VALUE
														}
														required
													/>
													<label
														id="label_input_25_0"
														htmlFor="input_25_0">
														{
															JLR_TALENT_AGREEMENT_VALUE
														}
													</label>
												</span>
												<a
													href="/"
													className="back-text">
													← Back to Program
												</a>
												<p className="required-fields-text">
													* Required fields
												</p>
											</div>
										</div>
									</li>

									{/* Hidden Fields */}
									<li style={{ display: "none" }}>
										Should be Empty:{" "}
										<input
											type="text"
											name="website"
											value=""
										/>
									</li>
								</ul>
							)}
						</div>

						{/* Additional Scripts */}
						<script>{`window.enableEventObserver=true`}</script>
						<script>{`JotForm.showJotFormPowered = "new_footer";`}</script>
						<script>{`JotForm.poweredByText = "Powered by Jotform";`}</script>
						<input
							type="hidden"
							className="simple_spc"
							id="simple_spc"
							name="simple_spc"
							value={FORM_ID}
						/>
						<script type="text/javascript">
							{`
                var all_spc = document.querySelectorAll("form[id='${FORM_ID}'] .simple_spc");
                for (var i = 0; i < all_spc.length; i++) {
                  all_spc[i].value = "${FORM_ID}-${FORM_ID}";
                }
              `}
						</script>
					</form>
				</div>
				<div className="summary-wrapper">
					<SelectionSummary
						variant="jlr"
						selections={{}}
						channelSelections={channels}
						jlrChannels={JLR_CHANNELS}
						showReimbursement={false}
						handleSelectionChange={() => {}}
					/>

					<div className="form-footer-text">
						{packageError && (
							<div className="error-message">
								*Please select at least one media channel to
								continue.
							</div>
						)}
					</div>

					<div className="form-submit-wrapper">
						{currentPage === 1 && (
							<>
								<button
									type="button"
									styleName="blue"
									size="large"
									onClick={handleNext}
									disabled={!anyChannelSelected}>
									CONTINUE →
								</button>
								{!anyChannelSelected ? (
									<p
										className="jlr-next-service-hint"
										role="status">
										Select at least one service to continue.
									</p>
								) : null}
							</>
						)}
						{currentPage === 2 && (
							<div className="form-submit-wrapper">
								<button
									type="button"
									styleName="outline"
									size="large"
									onClick={handleBack}
									style={{
										marginBottom: "10px",
										display: "none",
									}}>
									Back
								</button>
								<button
									type="submit"
									form={FORM_ID}
									styleName="blue"
									size="large"
									disabled={isSubmitDisabled}>
									Submit Enrollment →
								</button>
							</div>
						)}
					</div>
				</div>
			</div>
		</>
	);
};

export default JotFormComponent;
